import { ColumnHelper, GenerateColumnsFn } from "PFComponents/table/table.types";
import { sortingDateFn } from "PFComponents/table/table.utils";
import { Booking } from "PFTypes";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import css from "./bookings_table.module.scss";
import { ActionColumn } from "./columns/action_column";
import { BookingCategoryColumn } from "./columns/booking_category_column";
import { DatesColumn } from "./columns/dates_column";
import { TextColumn } from "./columns/text_column";
import { TotalHoursColumn } from "./columns/total_hours_column";
import { useSortRoles } from "./hooks/use_sort_roles";
import { useSortTitles } from "./hooks/use_sort_titles";
import { useSortTotalHours } from "./hooks/use_sort_total_hours";

enum ColumnId {
  BookingTitle = "bookingTitleColumn",
  Role = "roleColumn",
  BookingCategory = "bookingCategoryColumn",
  Dates = "datesColumn",
  TotalHours = "totalHoursColumn",
  Action = "actionColumn"
}

type UseBookingsListColumnsGeneratorReturn = GenerateColumnsFn<Booking>;

type UseBookingsListColumnsGenerator = {
  onBookingEdit?: (booking: Booking) => void;
  onBookingDelete?: VoidFunction;
  checkCanEditBooking?: (booking: Booking) => boolean;
  isDense?: boolean;
  withActions?: boolean;
};

export const useBookingsListColumnsGenerator = ({
  checkCanEditBooking,
  isDense,
  onBookingEdit,
  onBookingDelete,
  withActions
}: UseBookingsListColumnsGenerator): UseBookingsListColumnsGeneratorReturn => {
  const { t } = useTranslation("availability", { keyPrefix: "bookings.list" });
  const { sortTotalHours } = useSortTotalHours();
  const { sortRoles } = useSortRoles();
  const { sortTitles } = useSortTitles();

  return useCallback(
    (columnHelper: ColumnHelper<Booking>) => {
      const columns = {
        bookingTitleColumn: (columnHelper: ColumnHelper<Booking>) =>
          columnHelper.accessor((row) => row, {
            id: ColumnId.BookingTitle,
            cell: (info) => (
              <TextColumn
                booking={info.getValue()}
                text={info.getValue().title ?? t("untitled")}
                isDense={isDense}
              />
            ),
            header: () => <span className={css.tableHeader}>{t("columns.bookingTitle")}</span>,
            sortFn: (rowA, rowB) => sortTitles(rowA.original, rowB.original)
          }),
        roleColumn: (columnHelper: ColumnHelper<Booking>) =>
          columnHelper.accessor((row) => row, {
            id: ColumnId.Role,
            cell: (info) => (
              <TextColumn
                booking={info.getValue()}
                text={info.getValue().activity?.name ?? t("notApplicable")}
                isDense={isDense}
              />
            ),
            header: () => <span className={css.tableHeader}>{t("columns.activity")}</span>,
            sortFn: (rowA, rowB) => sortRoles(rowA.original, rowB.original)
          }),
        bookingCategoryColumn: (columnHelper: ColumnHelper<Booking>) =>
          columnHelper.accessor((row) => row, {
            id: ColumnId.BookingCategory,
            cell: (info) => <BookingCategoryColumn booking={info.getValue()} isDense={isDense} />,
            header: () => <span className={css.tableHeader}>{t("columns.bookingCategory")}</span>,
            sortFn: (rowA, rowB) => rowA.original.booking_category_id - rowB.original.booking_category_id
          }),
        datesColumn: (columnHelper: ColumnHelper<Booking>) =>
          columnHelper.accessor((row) => row, {
            id: ColumnId.Dates,
            cell: (info) => (
              <DatesColumn startDate={info.getValue().start_date} endDate={info.getValue().end_date} />
            ),
            header: () => <span className={css.tableHeader}>{t("columns.dates")}</span>,
            sortFn: (rowA, rowB) =>
              sortingDateFn(
                rowA.original?.start_date ? new Date(rowA.original?.start_date) : undefined,
                rowB.original?.start_date ? new Date(rowB.original?.start_date) : undefined
              )
          }),
        totalHoursColumn: (columnHelper: ColumnHelper<Booking>) =>
          columnHelper.accessor((row) => row, {
            id: ColumnId.TotalHours,
            cell: (info) => <TotalHoursColumn booking={info.getValue()} />,
            header: () => <span className={css.tableHeader}>{t("columns.totalHours")}</span>,
            sortFn: (rowA, rowB) => sortTotalHours(rowA.original, rowB.original)
          }),
        ...(!!withActions && !!checkCanEditBooking
          ? {
              actionColumn: (columnHelper: ColumnHelper<Booking>) =>
                columnHelper.accessor((row) => row, {
                  id: ColumnId.Action,
                  cell: (row) => (
                    <ActionColumn
                      booking={row.getValue()}
                      readOnly={!checkCanEditBooking(row.getValue())}
                      onEdit={onBookingEdit}
                      onDelete={onBookingDelete}
                    />
                  ),
                  header: () => <span className={css.tableHeader}></span>,
                  size: 45,
                  enableSorting: false
                })
            }
          : {})
      };
      return [
        columns.bookingTitleColumn(columnHelper),
        columns.roleColumn(columnHelper),
        columns.bookingCategoryColumn(columnHelper),
        columns.datesColumn(columnHelper),
        columns.totalHoursColumn(columnHelper),
        ...(columns.actionColumn ? [columns.actionColumn(columnHelper)] : [])
      ];
    },
    [checkCanEditBooking, isDense, onBookingEdit, sortRoles, sortTitles, sortTotalHours, t]
  );
};
